import type { DocumentShareDialogContentFragment } from "../client/generated";
import { Trans, useTranslation } from "react-i18next";
import { AccessButton } from "@acdc2/ui/components/access-button";
import { DocumentShareCombobox } from "./DocumentShareCombobox";
import { DocumentShareCollaborator } from "./DocumentShareCollaborator";
import { DocumentSharePermission } from "./DocumentSharePermission";
import { assertTypeName } from "../client/guards";
import {
  useDocumentShareDialogContentQuery,
  useShareDocumentMutation,
} from "../client/generated";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogLoading,
  DialogTitle,
} from "@acdc2/ui/components/dialog";

type Props = {
  fragment: DocumentShareDialogContentFragment;
  isOpen: boolean;
};

export function DocumentShareDialogContent({ fragment, isOpen }: Props) {
  const { t } = useTranslation();
  const [shareDocument] = useShareDocumentMutation();

  const { data } = useDocumentShareDialogContentQuery({
    variables: { documentId: fragment.id },
    skip: !isOpen,
  });

  if (!data) return <DialogLoading />;
  const { document } = data;
  assertTypeName(document?.__typename === "Document");

  const handleShare = async (userId: string) => {
    try {
      await shareDocument({
        variables: {
          documentId: document.id,
          userId,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <DialogContent showCloseButton={false}>
      <DialogHeader>
        <DialogTitle>{t("DocumentShareDialogContent.title")}</DialogTitle>
        <DialogDescription>
          <Trans
            i18nKey="DocumentShareDialogContent.description"
            components={{ "word-break": <span className="break-all" /> }}
            values={{ name: document.name }}
          />
        </DialogDescription>
      </DialogHeader>

      <DocumentShareCombobox
        fragment={document.viewerMembership.user.colleagues}
        onSelect={handleShare}
      />

      <div className="flex flex-col gap-3">
        <DialogTitle asChild>
          <h3>{t("DocumentShareDialogContent.collaborators.title")}</h3>
        </DialogTitle>
        <ul className="flex flex-col gap-4">
          {document.members.memberships.map((membership) => (
            <li key={membership.user.id}>
              <DocumentShareCollaborator
                fragment={membership.user}
                right={<DocumentSharePermission fragment={membership} />}
              />
            </li>
          ))}
        </ul>
      </div>

      <DialogFooter>
        <DialogClose asChild>
          <AccessButton>{t("DocumentShareDialogContent.done")}</AccessButton>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
}
